import {
  HttpClient,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  APP_INITIALIZER,
  ErrorHandler,
  Injector,
  NgModule,
} from '@angular/core';
import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { NgbDatepickerI18n, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonService } from '@geneplanet/ngx-shop/src/lib/shared';

import { Auth0Module } from '@geneplanet/ngx-auth0';

import { environment } from 'src/environments/environment';

import { MultipleFilesHttpTranslationsLoader } from './multiple-files-http-translations-loader';

import { SharedModule } from './shared/shared.module';
import { AppComponent } from './app.component';

import { httpInterceptorProviders } from './core/interceptors';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { CookiesModule } from './cookie-policy/cookies.module';

import { AppRoutingModule } from './app-routing.module';

// eslint-disable-next-line @typescript-eslint/naming-convention
import * as Sentry from '@sentry/angular';

import { RecaptchaV3Module, RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha';
import { ANALYTICS_SERVICE, RECAPTCHA } from 'src/app/app.constants';
import { CoreModule } from './core/core.module';
import { buildInfo } from 'src/environments/buildInfo';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { LanguageService } from './shared/services/language.service';
import { AnalyticsService as Analytics } from '@geneplanet/ngx-utils/src/lib/analytics';
import {
  catchError,
  filter,
  first,
  map,
  mapTo,
  tap,
  timeout,
} from 'rxjs/operators';
import { combineLatest, Observable, of } from 'rxjs';
import { NavigationStart, Router } from '@angular/router';
import { UserIntentState } from './shared/interfaces/intent.interface';
import { IntentStrategyService } from './shared/services/intent/intent-strategy.service';
import { IntentService } from './shared/services/intent/intent.service';
import { CustomPreloadingStrategy } from './core/utils/custom-preload-strategy';
import { NgbDatepickerI18nCustom } from './core/utils/custom-datepicker-i18n';
import { AnalyticsModule } from '@geneplanet/ngx-utils';
import {
  provideLokalise,
  withEnvironmentEnabled,
  withProjectId,
} from './lokalise';

const appInitializerFactory = (injector: Injector) => {
  return () => {
    const localize = injector.get(LocalizeRouterService);
    const common = injector.get(CommonService);
    const language = injector.get(LanguageService);
    const intentService = injector.get(IntentService);
    const intentStrategyService = injector.get(IntentStrategyService);
    const router = injector.get(Router);
    const analyticsService = injector.get(Analytics);

    if (environment.rudderStackEnabled) {
      analyticsService.init(
        'HI',
        environment.rudderstackKey,
        'https://geneplanetympr.dataplane.rudderstack.com'
      );
    }

    const localizeInitialized = localize.hooks.initialized.pipe(
      map(() => {
        const location = common.getLocationFromCode(
          localize.parser.currentLang
        );
        return language.onLocationChange(location);
      })
    );

    // magic link page track
    router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        first()
      )
      .subscribe((event: NavigationStart) => {
        if (event.url.startsWith('/nipt/view/')) {
          analyticsService.pageTrack(event.url);
        }
      });

    const intentInitialized = (): Observable<boolean> => {
      return intentService.fetchUserIntent().pipe(
        tap((state: UserIntentState) => {
          intentStrategyService.setStrategyByIntentId(state.intentId);
        }),
        mapTo(true),
        // Make sure API call doesn't block application bootstrap too long
        timeout(4000),
        // If Intent API call fails, whole application will not load.
        catchError((exception: any) => {
          if (exception?.name === 'TimeoutError') {
            Sentry.captureException(`[INTENT BOOTSTRAP] Request timeout!`);
          } else {
            Sentry.captureException(
              `[INTENT BOOTSTRAP] Exception: ${
                exception?.message ?? 'UNKNOWN!!'
              }`
            );
          }
          return of(true);
        })
      );
    };

    return combineLatest([
      localizeInitialized,
      intentInitialized(),
    ]).toPromise();
  };
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    NgbModule,
    InlineSVGModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    Auth0Module.forRoot(environment),
    CookiesModule,
    CoreModule,
    SharedModule,
    AppRoutingModule,
    RecaptchaV3Module,
    AnalyticsModule,
    HammerModule,
  ],
  providers: [
    provideLokalise(
      withProjectId('73322359621e0324812c59.72472535'),
      withEnvironmentEnabled(environment.environment !== 'production')
    ),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    LocalizeRouterService,
    httpInterceptorProviders,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: RECAPTCHA.siteKey },
    { provide: 'environment', useValue: environment },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      multi: true,
      deps: [Injector],
    },
    { provide: NgbDatepickerI18n, useClass: NgbDatepickerI18nCustom },
    CustomPreloadingStrategy,
    { provide: ANALYTICS_SERVICE, useExisting: Analytics },
    { provide: 'googleTagManagerId', useValue: environment.gtmContainer },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}

export function createTranslateLoader(http: HttpClient) {
  // Add version suffix to translation files, to make sure
  // latest translations are loaded
  const buildSuffix =
    String(buildInfo.buildNumber) !== 'dev'
      ? `?version=${buildInfo.buildNumber}`
      : '';
  return new MultipleFilesHttpTranslationsLoader(http, [
    { prefix: `./assets/i18n/`, suffix: `.json${buildSuffix}` },
    { prefix: `./assets/i18n/shop/`, suffix: `.json${buildSuffix}` },
  ]);
}
